import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Upload, Trash2, Plus, X, Save, LayoutDashboard, Flame, Snowflake, Anchor, DoorOpen, Bath, MapPin, Loader2 } from 'lucide-react';

const PIN_TYPES = {
  hot: { label: 'Zona caliente', color: '#dc2626', icon: Flame, bg: 'bg-red-500' },
  cold: { label: 'Zona fría', color: '#0891b2', icon: Snowflake, bg: 'bg-cyan-600' },
  anchor: { label: 'Ancla / tenant clave', color: '#1e3a8a', icon: Anchor, bg: 'bg-blue-900' },
  access: { label: 'Acceso / entrada', color: '#15803d', icon: DoorOpen, bg: 'bg-green-700' },
  restroom: { label: 'Servicios / baño', color: '#7c3aed', icon: Bath, bg: 'bg-purple-600' },
  other: { label: 'Otro', color: '#64748b', icon: MapPin, bg: 'bg-slate-500' },
};

export default function FloorPlanEditor({ placeId, placeName }) {
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [pinType, setPinType] = useState('hot');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedPin, setSelectedPin] = useState(null);
  const [dirty, setDirty] = useState(false);
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const fileRef = useRef(null);

  const loadPlans = async () => {
    if (!placeId) return;
    setLoading(true);
    try {
      const r = await api.get('/plaza-floor-plans', { params: { place_id: placeId } });
      setPlans(r.data || []);
      if (r.data?.[0] && !activePlan) {
        // load first plan
        const full = await api.get(`/plaza-floor-plans/${r.data[0].id}`);
        setActivePlan(full.data);
      }
    } catch { toast.error('Error cargando planos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) loadPlans(); }, [open, placeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Imagen demasiado grande (máx 5 MB)');
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = String(reader.result).split(',')[1];
        const img = new Image();
        img.onload = async () => {
          try {
            const r = await api.post('/plaza-floor-plans', {
              place_id: placeId,
              place_name: placeName,
              name: file.name.replace(/\.[^.]+$/, ''),
              image_data: b64,
              image_mime: file.type || 'image/png',
              width_px: img.naturalWidth,
              height_px: img.naturalHeight,
            });
            toast.success('Plano subido');
            // reload the full plan with image
            const full = await api.get(`/plaza-floor-plans/${r.data.id}`);
            setActivePlan(full.data);
            loadPlans();
          } catch { toast.error('Error subiendo plano'); }
          finally { setUploading(false); }
        };
        img.src = String(reader.result);
      };
      reader.readAsDataURL(file);
    } catch { toast.error('Error leyendo archivo'); setUploading(false); }
  };

  const handleImageClick = (e) => {
    if (!activePlan || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x_pct = ((e.clientX - rect.left) / rect.width) * 100;
    const y_pct = ((e.clientY - rect.top) / rect.height) * 100;
    if (x_pct < 0 || x_pct > 100 || y_pct < 0 || y_pct > 100) return;
    const newPin = { id: `tmp-${Date.now()}`, x_pct, y_pct, type: pinType, label: '', note: '' };
    setActivePlan({ ...activePlan, pins: [...(activePlan.pins || []), newPin] });
    setSelectedPin(newPin.id);
    setDirty(true);
  };

  const updatePin = (pinId, patch) => {
    const pins = activePlan.pins.map(p => p.id === pinId ? { ...p, ...patch } : p);
    setActivePlan({ ...activePlan, pins });
    setDirty(true);
  };

  const removePin = (pinId) => {
    setActivePlan({ ...activePlan, pins: activePlan.pins.filter(p => p.id !== pinId) });
    setSelectedPin(null);
    setDirty(true);
  };

  const savePins = async () => {
    try {
      await api.patch(`/plaza-floor-plans/${activePlan.id}/pins`, { pins: activePlan.pins });
      toast.success('Zonas guardadas');
      setDirty(false);
    } catch { toast.error('Error guardando'); }
  };

  const deletePlan = async () => {
    if (!confirm('¿Eliminar este plano y todas sus zonas?')) return;
    try {
      await api.delete(`/plaza-floor-plans/${activePlan.id}`);
      setActivePlan(null);
      loadPlans();
    } catch { toast.error('Error'); }
  };

  const imageSrc = activePlan?.image_data ? `data:${activePlan.image_mime || 'image/png'};base64,${activePlan.image_data}` : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-floor-plan" variant="outline" className="w-full">
          <LayoutDashboard size={14} className="mr-2" />Analizar interior de plaza
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-[1400px] p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2">
            <LayoutDashboard size={16} /> Análisis interior — {placeName}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-[280px_1fr] h-[80vh]">
          {/* Left: plans + toolbar + pins list */}
          <div className="border-r border-slate-200 overflow-y-auto p-4 bg-slate-50" data-testid="floor-plan-sidebar">
            {/* Plans */}
            <div className="label-caps mb-2">Planos ({plans.length})</div>
            {loading ? <Loader2 size={14} className="animate-spin text-slate-400" /> : (
              <div className="space-y-1 mb-4">
                {plans.map(p => (
                  <button key={p.id} onClick={async () => {
                    const full = await api.get(`/plaza-floor-plans/${p.id}`);
                    setActivePlan(full.data); setSelectedPin(null); setDirty(false);
                  }} className={`w-full text-left text-xs p-2 border ${activePlan?.id === p.id ? 'border-blue-900 bg-white' : 'border-slate-200 bg-white hover:border-slate-400'}`}>
                    <div className="font-semibold truncate">{p.name}</div>
                    <div className="text-slate-500">{(p.pins || []).length} zonas</div>
                  </button>
                ))}
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => handleUpload(e.target.files?.[0])} data-testid="upload-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn" variant="outline" size="sm" className="w-full">
              {uploading ? <><Loader2 size={12} className="mr-2 animate-spin" />Subiendo…</> : <><Upload size={12} className="mr-2" />Subir plano nuevo</>}
            </Button>

            {activePlan && (
              <>
                {/* Toolbar */}
                <div className="mt-6 label-caps mb-2">Tipo de zona</div>
                <div className="grid grid-cols-2 gap-1 mb-4">
                  {Object.entries(PIN_TYPES).map(([k, v]) => {
                    const Icon = v.icon;
                    return (
                      <button key={k} onClick={() => setPinType(k)} data-testid={`pin-type-${k}`}
                        className={`text-xs p-2 border flex items-center gap-1 ${pinType === k ? 'text-white border-transparent ' + v.bg : 'bg-white text-slate-700 border-slate-200'}`}>
                        <Icon size={11} />{v.label.split(' ')[0]}
                      </button>
                    );
                  })}
                </div>
                <div className="text-xs text-slate-500 mb-4">Haz click en el plano para agregar una zona <strong style={{color: PIN_TYPES[pinType].color}}>{PIN_TYPES[pinType].label}</strong>.</div>

                {/* Pin list */}
                <div className="label-caps mb-2">Zonas ({activePlan.pins?.length || 0})</div>
                <div className="space-y-1 mb-4 max-h-56 overflow-y-auto">
                  {(activePlan.pins || []).map(p => {
                    const meta = PIN_TYPES[p.type] || PIN_TYPES.other;
                    const Icon = meta.icon;
                    return (
                      <button key={p.id} onClick={() => setSelectedPin(p.id)} data-testid={`pin-${p.id}`}
                        className={`w-full text-left text-xs p-2 flex items-center gap-2 border ${selectedPin === p.id ? 'border-slate-900 bg-white' : 'border-slate-200 bg-white hover:border-slate-400'}`}>
                        <Icon size={10} style={{ color: meta.color }} />
                        <span className="flex-1 truncate">{p.label || meta.label}</span>
                        <button onClick={(e) => { e.stopPropagation(); removePin(p.id); }} className="text-slate-400 hover:text-red-700"><Trash2 size={10} /></button>
                      </button>
                    );
                  })}
                </div>

                {/* Selected pin editor */}
                {selectedPin && (() => {
                  const p = activePlan.pins.find(x => x.id === selectedPin);
                  if (!p) return null;
                  return (
                    <div className="border border-blue-200 bg-blue-50/40 p-3 space-y-2 mb-4">
                      <div className="text-xs font-semibold text-slate-800">Editar zona</div>
                      <Input value={p.label || ''} onChange={(e) => updatePin(p.id, { label: e.target.value })} placeholder="Nombre / etiqueta" className="text-xs h-8 bg-white" data-testid="pin-label" />
                      <textarea value={p.note || ''} onChange={(e) => updatePin(p.id, { note: e.target.value })} placeholder="Nota u observación…" className="w-full text-xs p-2 border border-slate-200 bg-white h-16 resize-none" data-testid="pin-note" />
                    </div>
                  );
                })()}

                <div className="flex gap-2">
                  <Button onClick={savePins} disabled={!dirty} data-testid="save-pins-btn" className="flex-1 bg-blue-900 hover:bg-blue-800" size="sm">
                    <Save size={12} className="mr-1" />Guardar
                  </Button>
                  <Button onClick={deletePlan} variant="ghost" size="sm" data-testid="delete-plan-btn"><Trash2 size={12} className="text-red-700" /></Button>
                </div>
              </>
            )}
          </div>

          {/* Right: image canvas */}
          <div className="bg-slate-100 overflow-auto flex items-center justify-center p-4">
            {!activePlan ? (
              <div className="text-center text-slate-400 text-sm">
                <Upload size={32} className="mx-auto mb-2 text-slate-300" />
                Sube un plano para empezar el análisis interior.
              </div>
            ) : (
              <div ref={containerRef} onClick={handleImageClick} className="relative inline-block cursor-crosshair bg-white shadow-lg" data-testid="floor-plan-canvas">
                {imageSrc && <img ref={imgRef} src={imageSrc} alt="Plano" className="block max-h-[70vh] max-w-[100%]" draggable={false} />}
                {(activePlan.pins || []).map(p => {
                  const meta = PIN_TYPES[p.type] || PIN_TYPES.other;
                  const Icon = meta.icon;
                  const isSelected = selectedPin === p.id;
                  return (
                    <div key={p.id}
                      onClick={(e) => { e.stopPropagation(); setSelectedPin(p.id); }}
                      className={`absolute -translate-x-1/2 -translate-y-full flex flex-col items-center cursor-pointer transition-transform ${isSelected ? 'scale-125 z-10' : 'z-0 hover:scale-110'}`}
                      style={{ left: `${p.x_pct}%`, top: `${p.y_pct}%` }}
                      data-testid={`canvas-pin-${p.id}`}
                    >
                      {p.label && <div className="text-[10px] px-1.5 py-0.5 bg-white shadow font-semibold text-slate-800 mb-1 whitespace-nowrap">{p.label}</div>}
                      <div className="rounded-full p-1.5 shadow-lg border-2 border-white" style={{ backgroundColor: meta.color }}>
                        <Icon size={12} color="white" />
                      </div>
                      <div className="w-0.5 h-2" style={{ backgroundColor: meta.color }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
